import L from 'leaflet';
import React, { forwardRef } from 'react';
import { MapContainer, MapContainerProps } from 'react-leaflet';

// react-leaflet@4.2.1 bug: MapContainer's ref callback checks `context === null`
// through a stale closure, so when React re-attaches the ref to the same DOM node
// (Suspense hide/reveal, Fast Refresh) it creates a second L.Map on a div that
// already has one and throws "Map container is already initialized".
// Recovery: remount the map with a new key so it gets a fresh container div.

const MAX_RECOVERY_ATTEMPTS = 3;

interface RecoveryState {
  mapKey: number;
}

class MapRecoveryBoundary extends React.Component<
  { children: React.ReactNode },
  RecoveryState
> {
  state: RecoveryState = { mapKey: 0 };

  componentDidCatch(error: Error) {
    if (
      error?.message?.includes('Map container is already initialized') &&
      this.state.mapKey < MAX_RECOVERY_ATTEMPTS
    ) {
      this.setState((prev) => ({ mapKey: prev.mapKey + 1 }));
    } else {
      throw error;
    }
  }

  render() {
    return (
      <React.Fragment key={this.state.mapKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}

const SafeMapContainer = forwardRef<L.Map, MapContainerProps>((props, ref) => (
  <MapRecoveryBoundary>
    <MapContainer ref={ref} {...props} />
  </MapRecoveryBoundary>
));

SafeMapContainer.displayName = 'SafeMapContainer';

export default SafeMapContainer;
